import { Alert, Checkbox, Container, FormField, Grid, Header, Input } from "@cloudscape-design/components"
import AppConfig from "configs/AppConfig";
import { useEffect } from "react";
import { ClusterInvoiceStepProp } from "types/invoiceTypes";
import OrderSummaryCard from "./OrderSummaryCard";
import {freeTrialPeriodDays} from "configs/settings";


function ClusterInvoiceStep({
    invoiceData, 
    setInvoiceData, 
    slaCheckbox, 
    setSlaCheckbox, 
    slaCheckboxError, 
    setSlaCheckboxError, 
    hasUsedFreeTrial,
    securityAddOns,
    orderSummary,
    selectedPlan,
    selectedSize,
    deploymentData
}:ClusterInvoiceStepProp) {

    // Update Invoice Data
    useEffect(()=>{
        setInvoiceData((prev)=>({...prev, dueDate: orderSummary.dueDate, price: orderSummary.totalPrice}))
    },[orderSummary.dueDate, orderSummary.subtotal]);

    return (
        <Grid
            gridDefinition={[
                { colspan: { default: 12, xs: 7, s:8, l: 8, xl: 9 } },
                { colspan: { default: 12, xs: 5, s:4, l: 4, xl: 3 } }
            ]}
        >
            {/* Invoice Information section */}
            <Container header={<Header variant="h3">Invoice Information</Header>}>
                <br />

                {/* Fist Line */}
                <Grid
                    gridDefinition={[
                        { colspan: { default: 12, xxs: 6 } },
                        { colspan: { default: 12, xxs: 6 } }
                    ]}
                >
                    <FormField 
                        errorText={invoiceData.errors?.firstName} 
                        label={<>First Name <span className="required-field">*</span></>}
                    >
                        <Input 
                            value={invoiceData.firstName} 
                            placeholder="Enter your first name" 
                            onChange={({detail})=>{setInvoiceData((prev)=>({...prev, firstName: detail.value, errors: { ...prev.errors, firstName: undefined }}))}} 
                        />
                    </FormField>

                    <FormField errorText={invoiceData.errors?.lastName} label={<>Last Name <span className="required-field">*</span></>}>
                        <Input 
                            value={invoiceData.lastName} 
                            placeholder="Enter your last name" 
                            onChange={({detail})=>{setInvoiceData((prev)=>({...prev, lastName: detail.value, errors: { ...prev.errors, lastName: undefined }}))}} 
                        />
                    </FormField>
                </Grid>

                {/* Second Line */}
                <Grid
                    gridDefinition={[
                        { colspan: { default: 12, xxs: 6 } },
                        { colspan: { default: 12, xxs: 6 } }
                    ]}
                >
                    <FormField errorText={invoiceData.errors?.billingEmail} label={<>Billing Email <span className="required-field">*</span></>} stretch>
                        <Input 
                            value={invoiceData.billingEmail} 
                            placeholder="billing@company.com" 
                            onChange={({detail})=>{
                                let error: string | undefined;

                                if(!detail.value){
                                    error = undefined;
                                } 
                                else if(detail.value.startsWith(".") || detail.value.endsWith(".")){
                                    error = "Email cannot start or end with a dot."
                                } 
                                else if(detail.value.startsWith("-") || detail.value.endsWith("-")){
                                    error = "Email cannot start or end with hyphen."
                                };

                                setInvoiceData((prev)=>({...prev, billingEmail: detail.value, errors: { ...prev.errors, billingEmail: error }}))
                            }} 
                        />
                    </FormField>

                    <FormField errorText={invoiceData.errors?.phoneNumber} label={<>Phone Number <span className="required-field">*</span></>} stretch>
                        <Input 
                            value={invoiceData.phoneNumber} 
                            placeholder="+1 (555) 000-0000" 
                            onChange={({detail})=>{
                                setInvoiceData((prev)=>({...prev, phoneNumber: detail.value, errors: { ...prev.errors, phoneNumber: undefined }}))
                            }} 
                        />
                    </FormField>
                </Grid>

                {/* Third Line */}
                <Grid
                    gridDefinition={[
                        { colspan: 12 }
                    ]}
                >
                    <FormField errorText={invoiceData.errors?.company} label={<>Company <span className="required-field">*</span></>} stretch>
                        <Input 
                            value={invoiceData.company} 
                            placeholder="Organization Inc." 
                            onChange={({detail})=>{
                                setInvoiceData((prev)=>({...prev, company: detail.value, errors: { ...prev.errors, company: undefined }}))
                            }} 
                        />
                    </FormField>
                </Grid>

                {/* Fourth Line */}
                <Grid
                    gridDefinition={[
                        { colspan: 12 }
                    ]}
                >
                    <FormField errorText={invoiceData.errors?.billingAddress} label={<>Billing Address <span className="required-field">*</span></>} stretch>
                        <Input 
                            value={invoiceData.billingAddress} 
                            placeholder="123 Infrastructure Way, Suite 400" 
                            onChange={({detail})=>{
                                setInvoiceData((prev)=>({...prev, billingAddress: detail.value, errors: { ...prev.errors, billingAddress: undefined }}))
                            }} 
                        />
                    </FormField>
                </Grid>

                {/* Fifth Line */}
                <Grid
                    gridDefinition={[
                        { colspan: { default: 12, xxs: 6 } },
                        { colspan: { default: 12, xxs: 6 } }
                    ]}
                >
                    <FormField errorText={invoiceData.errors?.city} label={<>City <span className="required-field">*</span></>}>
                        <Input 
                            value={invoiceData.city} 
                            placeholder="San Francisco" 
                            onChange={({detail})=>{
                                setInvoiceData((prev)=>({...prev, city: detail.value, errors: { ...prev.errors, city: undefined }}))
                            }} 
                        />
                    </FormField>

                    <FormField errorText={invoiceData.errors?.postalCode} label={<>Postal Code <span className="required-field">*</span></>}>
                        <Input 
                            value={invoiceData.postalCode} 
                            placeholder="94105" 
                            onChange={({detail})=>{
                                setInvoiceData((prev)=>({...prev, postalCode: detail.value, errors: { ...prev.errors, postalCode: undefined }}))
                            }} 
                        />
                    </FormField>
                </Grid>

                <br />
                <br />

                {/* Provisioning Note */}
                <Alert type="info" header="Provisioning Note">
                    <ul className="p-0">
                        <li>Provisioning will begin immediately after confirmation.</li>
                        <li>You will receive an invoice by email.</li>
                        <li>Please complete payment within <strong>{hasUsedFreeTrial ? "3 days" : `${freeTrialPeriodDays} + 3 days`}</strong> via bank transfer.</li>
                    </ul>
                </Alert>
            </Container>

            {/* Order Summary section */}
            <div className="order-summary-column">
                <OrderSummaryCard
                    orderSummary={orderSummary}
                    currency={selectedPlan?.size[selectedSize].price?.currency ?? ""}
                    securityAddOns={securityAddOns}
                    hasUsedFreeTrial={hasUsedFreeTrial}
                    deploymentData={deploymentData}
                />    
            </div>

            {/* SLA Agreement Section */}
            <Container>
                <FormField errorText={slaCheckboxError}>
                    <Checkbox
                        onChange={({ detail }) =>{
                            setSlaCheckbox(detail.checked);
                            setSlaCheckboxError(undefined);
                        }}
                        checked={slaCheckbox}
                    >
                        I confirm that the billing information provided is accurate and I authorize Aswar.io to provision the {selectedPlan?.name} Cluster. I have read and agree to the <a href={AppConfig.SERVICE_AGREEMENT_URL} target="_blank" rel="noopener noreferrer">Service Level Agreement (SLA)</a>
                    </Checkbox>
                </FormField>
            </Container>
        </Grid>
    )
}

export default ClusterInvoiceStep
